/**
 * Unico punto di contatto con le API.
 *
 * Tutte le scritture passano da qui, e nessuna di esse tocca lo stato locale in
 * modo definitivo: il server è l'unica fonte della verità sui chip. Il client
 * può mostrare un'anteprima ottimistica, ma se il server risponde di no quella
 * anteprima viene ritirata.
 */

export type ApiErrorBody = {
  error?: string;
  code?: string;
  message?: string;
  [key: string]: unknown;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
    message?: string,
  ) {
    super(message ?? body.message ?? 'Richiesta non riuscita.');
    this.name = 'ApiError';
  }

  /** Codice applicativo: `already_returned`, `last_admin`, `validation`, … */
  get code(): string | undefined {
    return this.body.code ?? this.body.error;
  }
}

/**
 * La richiesta non è nemmeno partita, o la risposta non è arrivata: rete assente,
 * server irraggiungibile, connessione caduta a metà. È distinta da un errore
 * applicativo perché la reazione giusta è diversa — qui si blocca l'interfaccia
 * e si aspetta, non si mostra un messaggio di errore che l'operatore non può
 * risolvere.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Connessione al server non disponibile.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Chi vuole sapere quando una richiesta non è nemmeno partita.
 *
 * L'interfaccia deve accorgersi della caduta di rete **subito**, non al
 * prossimo controllo periodico: se l'operatore preme "Restituito" e la rete è
 * caduta un istante prima, deve vederlo in quel momento invece di continuare a
 * premere. Chi si iscrive qui riceve la notizia al primo tentativo fallito.
 */
type NetworkFailureListener = () => void;

const networkFailureListeners = new Set<NetworkFailureListener>();

export function onNetworkFailure(listener: NetworkFailureListener): () => void {
  networkFailureListeners.add(listener);
  return () => networkFailureListeners.delete(listener);
}

function reportNetworkFailure(): void {
  for (const listener of networkFailureListeners) listener();
}

export type ApiFetchOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal } = options;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      // Il cookie di sessione è `httpOnly` e viaggia solo se lo chiediamo
      // esplicitamente: senza questo, da una pagina servita sullo stesso
      // dominio, le richieste partirebbero senza credenziali.
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    reportNetworkFailure();
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorBody(response));
  }

  return (await readSuccessBody<T>(response)) as T;
}

/** Scarica un file binario (l'export xlsx) senza interpretarlo come JSON. */
export async function apiDownload(
  path: string,
): Promise<{ blob: Blob; fileName: string | null }> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: 'same-origin' });
  } catch (cause) {
    reportNetworkFailure();
    throw new NetworkError(cause);
  }

  if (!response.ok) throw new ApiError(response.status, await readErrorBody(response));

  return {
    blob: await response.blob(),
    fileName: fileNameFromHeader(response.headers.get('content-disposition')),
  };
}

async function readSuccessBody<T>(response: Response): Promise<T | undefined> {
  // 204 e le risposte senza corpo non sono errori: sono successi senza dati.
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as T;
  } catch (cause) {
    // Non è un problema di rete: il server ha risposto, con qualcosa che non
    // sappiamo leggere. Segnalarlo come "connessione assente" manderebbe
    // l'operatore a controllare il wifi per un guasto che non è suo.
    throw new Error('Risposta del server non leggibile.', { cause });
  }
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  const text = await response.text();
  if (text === '') return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as ApiErrorBody) : {};
  } catch {
    return {};
  }
}

function fileNameFromHeader(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? null;
}

/** Messaggio pronto da mostrare all'operatore per un errore qualsiasi. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof NetworkError) return 'Connessione assente. Riprova quando torna la rete.';
  if (error instanceof Error) return error.message;
  return 'Si è verificato un errore imprevisto.';
}

/**
 * L'errore merita di bloccare l'interfaccia? Solo quello di rete: gli altri
 * sono risposte del server, e il server ha risposto.
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof NetworkError;
}
